import { levelAt, siteBox, worldToRadar, type Level, type MapId } from "@69ly/maps";
import { BOMB_SECONDS, DEFAULT_REACH, type PlantPositionInput, type PlantSpot, type Site } from "@69ly/shared";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { GrenadeGlyph } from "./GrenadeGlyph.tsx";
import { AtPoint, OnMap, ThrowLine } from "./Radar.tsx";

/** Where a player looks: the end of their view line, in world units. */
export function aimPoint(p: { x: number; y: number; yaw: number; reach?: number | null }): { x: number; y: number } {
  const r = p.reach ?? DEFAULT_REACH;
  const rad = (p.yaw * Math.PI) / 180;
  // CS2 yaw: 0 = east, 90 = north, as the world's x and y.
  return { x: p.x + Math.cos(rad) * r, y: p.y + Math.sin(rad) * r };
}

/** The angle on screen (degrees, clockwise from the right) from a position to where it looks. */
export function screenAngle(map: MapId, p: { x: number; y: number; yaw: number; reach?: number | null }): number {
  const a = worldToRadar(map, p.x, p.y);
  const b = worldToRadar(map, aimPoint(p).x, aimPoint(p).y);
  return (Math.atan2(b.v - a.v, b.u - a.u) * 180) / Math.PI;
}

/**
 * A player on the radar, as on the strat board: a disc with their number in
 * their slot's colour and a wedge pointing where they look. Their name shows on hover.
 */
export function PlayerToken({ map, p, title }: { map: MapId; p: PlantPositionInput; title: string }) {
  return (
    <span className={`pp-token slot-${p.slot}`} style={{ "--angle": `${screenAngle(map, p)}deg` } as CSSProperties} title={title}>
      <span className="pp-facing" aria-hidden="true" />
      <span className="pp-number">{p.slot}</span>
    </span>
  );
}

/** Each player's view line: from them to where they look, dashed in their colour. */
export function ViewLines({ map, positions }: { map: MapId; positions: PlantPositionInput[] }) {
  return (
    <svg className="pp-views" viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden="true">
      {positions.map((p, i) => {
        const a = worldToRadar(map, p.x, p.y);
        const end = aimPoint(p);
        const b = worldToRadar(map, end.x, end.y);
        return (
          <g key={i} className={`slot-${p.slot}`}>
            <line className="pp-view-shadow" x1={a.u * 1000} y1={a.v * 1000} x2={b.u * 1000} y2={b.v * 1000} />
            <line className="pp-view" x1={a.u * 1000} y1={a.v * 1000} x2={b.u * 1000} y2={b.v * 1000} />
          </g>
        );
      })}
    </svg>
  );
}

/** A position's name on the map: its label, and who plays it. */
export const positionTitle = (p: PlantSpot["positions"][number] | PlantPositionInput, t: (k: string, o?: Record<string, unknown>) => string) =>
  [p.label || t("plant.playerN", { n: p.slot }), "member" in p && p.member ? p.member.name : null].filter(Boolean).join(" · ");

export interface PlantLayers {
  utility: boolean;
  positions: boolean;
  cones: boolean;
}

/** A plant spot's layers on the radar: utility thrown, where each player holds, where they look. */
export function PlantOnMap({ map, plant, level, layers }: { map: MapId; plant: PlantSpot; level: Level; layers: PlantLayers }) {
  const { t } = useTranslation();
  return (
    <>
      {layers.cones && <ViewLines map={map} positions={plant.positions.filter((p) => levelAt(map, p.z ?? plant.plant.z ?? 0) === level)} />}
      {layers.utility &&
        plant.utility.map((u, i) => (
          <div key={`u${i}`} className={`exec-layer slot-${u.slot}`}>
            <ThrowLine map={map} from={u.stuff.throw} to={u.stuff.landing} />
            {levelAt(map, u.stuff.throw.z) === level && (
              <OnMap map={map} x={u.stuff.throw.x} y={u.stuff.throw.y}>
                <span className={`slot-ring slot-${u.slot}`} />
              </OnMap>
            )}
            {levelAt(map, u.stuff.landing.z ?? u.stuff.throw.z) === level && (
              <OnMap map={map} x={u.stuff.landing.x} y={u.stuff.landing.y}>
                <span className={`marker is-static side-${u.stuff.side}`} title={u.stuff.title}>
                  <GrenadeGlyph grenade={u.stuff.grenade} size={14} />
                </span>
              </OnMap>
            )}
          </div>
        ))}
      {layers.positions &&
        plant.positions.map((p, i) =>
          levelAt(map, p.z ?? plant.plant.z ?? 0) === level ? (
            <OnMap key={`p${i}`} map={map} x={p.x} y={p.y}>
              <PlayerToken map={map} p={p} title={positionTitle(p, t)} />
            </OnMap>
          ) : null,
        )}
    </>
  );
}

/** The bomb timer from 0:40 to 0:00, one lane per post-plant throw so overlaps show. */
export function BombTimer({ plant }: { plant: PlantSpot }) {
  const { t } = useTranslation();
  if (plant.utility.length === 0) return <p className="muted small">{t("plant.noUtility")}</p>;
  const pos = (s: number) => ((BOMB_SECONDS - s) / BOMB_SECONDS) * 100;
  const ticks = [40, 35, 30, 25, 20, 15, 10, 5, 0];
  return (
    <div className="timeline-grid bomb-grid">
      <div className="timeline-axis" aria-hidden="true">
        {ticks.map((s) => (
          <span key={s} className="timeline-tick" style={{ left: `${pos(s)}%` }}>
            0:{String(s).padStart(2, "0")}
          </span>
        ))}
      </div>
      {plant.utility.map((u, i) => (
        <div key={i} className="timeline-lane">
          <div className="timeline-who">
            <span className={`slot-dot slot-${u.slot}`} />
            <span className="timeline-role">{t("plant.playerN", { n: u.slot })}</span>
          </div>
          <div className="timeline-track bomb-track">
            {ticks.map((s) => (
              <span key={s} className="timeline-gridline" style={{ left: `${pos(s)}%` }} aria-hidden="true" />
            ))}
            <span
              className={`bomb-bar slot-${u.slot}`}
              style={{ left: `${pos(u.from)}%`, width: `${Math.max(pos(u.to) - pos(u.from), 3)}%` }}
              title={`${u.stuff.title} · 0:${String(u.from).padStart(2, "0")}→0:${String(u.to).padStart(2, "0")}`}
            >
              <GrenadeGlyph grenade={u.stuff.grenade} size={12} />
              <span className="bomb-bar-text">{u.stuff.title}</span>
              <span className="timeline-chip-when">
                0:{String(u.from).padStart(2, "0")}→0:{String(u.to).padStart(2, "0")}
              </span>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Where the bomb can go down on a site: a dashed outline and its letter. */
export function SiteOutline({ map, site, level, active, tone }: { map: MapId; site: Site; level: Level; active: boolean; tone?: "warn" }) {
  const { t } = useTranslation();
  const b = siteBox(map, site);
  if (!b || b.level !== level) return null;
  const k = 1000;
  return (
    <>
      <svg className={`site-outline${active ? " is-active" : ""}${tone ? ` is-${tone}` : ""}`} viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden="true">
        <rect x={b.u0 * k} y={b.v0 * k} width={(b.u1 - b.u0) * k} height={(b.v1 - b.v0) * k} rx={4} />
      </svg>
      <AtPoint u={b.u0} v={b.v0} className="site-letter-spot">
        <span className={`site-letter${active ? " is-active" : ""}${tone ? ` is-${tone}` : ""}`} title={t(`area.${site}`)}>
          {t(`areaShort.${site}`)}
        </span>
      </AtPoint>
    </>
  );
}
